// Usage is metered as REAL COST IN MICRO-EUROS against a single rolling WEEKLY budget (resets 7 days
// after the first request); images reset 1 week after. `monthlyBudgetMicro` is the plan's monthly COGS
// ceiling at IONOS list prices (prompt + completion, so a long context can't run up unbilled cost);
// the enforced window is a quarter of it (see WEEKS_PER_MONTH), which keeps a burst week bounded while
// the month as a whole stays under the plan price. There is NO normalization/weighting layer any more -
// a request costs exactly what MODEL_PRICING says it costs.
export const PLANS = {
	FREE: {
		id: 'FREE',
		name: 'Free',
		price: 0,
		currency: 'EUR',
		interval: 'month' as const,
		features: [
			'Community & standard models',
			'~200 messages / week',
			// '15 AI images / week', // image generation temporarily disabled (see IMAGE_GENERATION_ENABLED)
			'Standard response time',
			'Community support',
		],
		limits: {
			requestsPerDay: 100,
			// Monthly cost ceiling in micro-euros (€2.00). Enforced as a quarter of this per rolling 7 days.
			monthlyBudgetMicro: 2_000_000,
			// Generated images per weekly session — tracked separately from the cost budget
			weeklyImages: 15,
		},
	},
	HOBBY: {
		id: 'HOBBY',
		name: 'Hobby',
		price: 999,
		// Annual total (cents) = monthly − YEARLY_DISCOUNT_PERCENT, ×12. Must match the Stripe yearly price.
		priceYearly: 10188,
		currency: 'EUR',
		interval: 'month' as const,
		stripePriceId: process.env.STRIPE_PRICE_HOBBY || '',
		stripePriceIdYearly: process.env.STRIPE_PRICE_HOBBY_YEARLY || '',
		features: [
			'All standard & medium models',
			'~500 messages / week',
			// '30 AI images / week', // image generation temporarily disabled (see IMAGE_GENERATION_ENABLED)
			'Priority response time',
			'Email support',
		],
		limits: {
			requestsPerDay: 1000,
			// €5.00 / month
			monthlyBudgetMicro: 5_000_000,
			weeklyImages: 30,
		},
	},
	STANDARD: {
		id: 'STANDARD',
		name: 'Standard',
		price: 2000,
		priceYearly: 20400,
		currency: 'EUR',
		interval: 'month' as const,
		stripePriceId: process.env.STRIPE_PRICE_STANDARD || '',
		stripePriceIdYearly: process.env.STRIPE_PRICE_STANDARD_YEARLY || '',
		features: [
			'All models incl. Qwen3-Coder-Next',
			'~1,100 messages / week',
			// '60 AI images / week', // image generation temporarily disabled (see IMAGE_GENERATION_ENABLED)
			'Priority response time',
			'Email support',
		],
		limits: {
			requestsPerDay: 5000,
			// €11.00 / month
			monthlyBudgetMicro: 11_000_000,
			weeklyImages: 60,
		},
	},
	PRO: {
		id: 'PRO',
		name: 'Pro',
		price: 8500,
		priceYearly: 86700,
		currency: 'EUR',
		interval: 'month' as const,
		stripePriceId: process.env.STRIPE_PRICE_PRO || '',
		stripePriceIdYearly: process.env.STRIPE_PRICE_PRO_YEARLY || '',
		features: [
			'All available models',
			'~4,500 messages / week',
			// '180 AI images / week', // image generation temporarily disabled (see IMAGE_GENERATION_ENABLED)
			'Fastest response time',
			'24/7 priority support',
			'Advanced analytics',
		],
		limits: {
			requestsPerDay: -1,
			// €45.00 / month
			monthlyBudgetMicro: 45_000_000,
			weeklyImages: 180,
		},
	},
} as const

export type PlanId = keyof typeof PLANS
export type Plan = (typeof PLANS)[PlanId]

export function getPlanById(planId: string): Plan | undefined {
	return PLANS[planId as PlanId]
}

// The plan allowance is quoted per MONTH but enforced per rolling WEEK, so a single heavy week cannot
// spend the whole month's budget. Four is deliberate (not 365/12/7 ≈ 4.35): it under-shoots slightly,
// which keeps the monthly ceiling a true ceiling rather than one a user can drift past.
export const WEEKS_PER_MONTH = 4

/** Monthly cost ceiling for a plan, in micro-euros (millionths of 1 EUR) */
export function getMonthlyBudgetMicro(planId: string): number {
	return (PLANS[planId as PlanId] || PLANS.FREE).limits.monthlyBudgetMicro
}

/** Per-7-day cost budget for a plan, in micro-euros — the figure actually enforced per session */
export function getWeeklyBudgetMicro(planId: string): number {
	return Math.round(getMonthlyBudgetMicro(planId) / WEEKS_PER_MONTH)
}

/** Per-weekly-session generated-image allowance for a plan (count, tracked separately from tokens) */
export function getSessionImageLimit(planId: string): number {
	return (PLANS[planId as PlanId] || PLANS.FREE).limits.weeklyImages
}

// Rough public estimate only: an average chat turn is ~3,500 total tokens (prompt incl. the windowed
// history + completion), which on the default gpt-oss-120b costs ~800 micro-euros; the figure below is
// held well above that because meta calls (title, suggestions, memory, classifiers) and especially
// web-search turns (a Linkup query alone is 5,000 micro-euros) pull the average up. Deliberately
// conservative so the "~N messages" plan copy under-promises. Drives ONLY that copy - the real budget
// is euros. If you edit monthlyBudgetMicro, refresh the "~N messages / week" feature strings.
export const EST_COST_PER_REQUEST_MICRO = 2500

/** Vague "messages per week" estimate for marketing copy, derived from the weekly cost budget. */
export function estimatedRequestsPerWeek(planId: string): number {
	return Math.round(getWeeklyBudgetMicro(planId) / EST_COST_PER_REQUEST_MICRO)
}

export type BillingInterval = 'month' | 'year'

/** Matches against both the monthly and yearly Stripe price of every plan. */
export function getPlanByStripePriceId(priceId: string): Plan | undefined {
	return Object.values(PLANS).find(
		(plan) =>
			('stripePriceId' in plan && plan.stripePriceId === priceId) ||
			('stripePriceIdYearly' in plan && plan.stripePriceIdYearly === priceId)
	)
}

/** Resolve a Stripe price id to a plan id (covers every paid tier + interval), falling back to FREE. */
export function mapPriceIdToPlan(priceId: string | null | undefined): PlanId {
	if (!priceId) return 'FREE'
	return (getPlanByStripePriceId(priceId)?.id as PlanId) ?? 'FREE'
}

/** Whether a Stripe price id is one of the yearly plan prices. */
export function intervalOfPriceId(priceId: string | null | undefined): BillingInterval {
	if (!priceId) return 'month'
	const yearly = Object.values(PLANS).some(
		(plan) => 'stripePriceIdYearly' in plan && plan.stripePriceIdYearly === priceId
	)
	return yearly ? 'year' : 'month'
}

/** The Stripe price id for a plan at the given interval (undefined for FREE / unconfigured). */
export function planStripePriceId(planId: string, interval: BillingInterval): string | undefined {
	const plan = PLANS[planId as PlanId]
	if (!plan || !('stripePriceId' in plan)) return undefined
	const id = interval === 'year' ? plan.stripePriceIdYearly : plan.stripePriceId
	return id || undefined
}

/** Total charge for a plan at the given interval, in cents (yearly = annual total). */
export function planTotalPrice(planId: string, interval: BillingInterval): number {
	const plan = PLANS[planId as PlanId] || PLANS.FREE
	return interval === 'year' && 'priceYearly' in plan ? plan.priceYearly : plan.price
}

/** Per-month-equivalent price for display, in cents (yearly total ÷ 12). */
export function planMonthlyEquivalent(planId: string, interval: BillingInterval): number {
	const plan = PLANS[planId as PlanId] || PLANS.FREE
	if (interval === 'year' && 'priceYearly' in plan) return Math.round(plan.priceYearly / 12)
	return plan.price
}

export const PLAN_HIERARCHY: Record<PlanId, number> = {
	FREE: 0,
	HOBBY: 1,
	STANDARD: 2,
	PRO: 3,
}

export function isUpgrade(from: PlanId, to: PlanId): boolean {
	return PLAN_HIERARCHY[to] > PLAN_HIERARCHY[from]
}

export function isDowngrade(from: PlanId, to: PlanId): boolean {
	return PLAN_HIERARCHY[to] < PLAN_HIERARCHY[from]
}

export function formatPrice(priceInCents: number, currency: string = 'EUR'): string {
	return new Intl.NumberFormat('de-DE', {
		style: 'currency',
		currency,
	}).format(priceInCents / 100)
}

// Extra-usage credit packages (prepaid, 1:1 — €10 of credit buys €10 of usage at cost). `amount` and
// `price` are both in euro-cents.
export const CREDIT_PACKAGES = [
	{ id: 'credits_10', amount: 1000, price: 1000, label: '10,00 EUR' },
	{ id: 'credits_25', amount: 2500, price: 2500, label: '25,00 EUR' },
	{ id: 'credits_50', amount: 5000, price: 5000, label: '50,00 EUR' },
	{ id: 'credits_100', amount: 10000, price: 10000, label: '100,00 EUR' },
] as const

// i18n key helper – replaces dots with underscores to avoid next-intl nesting
function descKey(id: string) {
	return id.replaceAll('.', '_')
}

// Available chat / completion models — the curated set we actually serve (mirrors CHAT_MODELS)
export const MODELS = [
	{ id: 'qwen3.5-9b', name: 'Qwen3.5 9B', params: '9B', category: 'SMALL', descKey: descKey('qwen3.5-9b') },
	{
		id: 'mistral-small-22b',
		name: 'Mistral Small 24B',
		params: '24B',
		category: 'MEDIUM',
		descKey: descKey('mistral-small-22b'),
	},
	{ id: 'llama-3.3-70b', name: 'Llama 3.3 70B', params: '70B', category: 'EXPERT', descKey: descKey('llama-3.3-70b') },
	{
		id: 'qwen3-coder-next',
		name: 'Qwen3-Coder-Next',
		params: '3B active (MoE)',
		category: 'EXPERT',
		descKey: descKey('qwen3-coder-next'),
	},
	{
		id: 'gpt-oss-120b',
		name: 'GPT-OSS 120B',
		params: '5.1B active (MoE)',
		category: 'EXPERT',
		descKey: descKey('gpt-oss-120b'),
	},
] as const

// Available image generation models
export const IMAGE_MODELS = [
	{ id: 'z-image-turbo', name: 'Z Image Turbo', params: '12B', descKey: descKey('z-image-turbo') },
	{ id: 'qwen-image-turbo', name: 'Qwen Image Turbo', params: '8B', descKey: descKey('qwen-image-turbo') },
	{ id: 'flux-2-dev', name: 'FLUX.2 Dev', params: '12B', descKey: descKey('flux-2-dev') },
] as const

// Available embedding models
export const EMBEDDING_MODELS = [
	{
		id: 'nomic-embed-text-v1.5',
		name: 'Nomic Embed Text v1.5',
		params: '137M',
		descKey: descKey('nomic-embed-text-v1.5'),
	},
	{ id: 'bge-m3', name: 'BGE-M3', params: '568M', descKey: descKey('bge-m3') },
	{
		id: 'gte-multilingual-base',
		name: 'GTE Multilingual Base',
		params: '305M',
		descKey: descKey('gte-multilingual-base'),
	},
	{
		id: 'multilingual-e5-large',
		name: 'Multilingual E5 Large',
		params: '560M',
		descKey: descKey('multilingual-e5-large'),
	},
	{
		id: 'snowflake-arctic-embed-l',
		name: 'Snowflake Arctic Embed L',
		params: '335M',
		descKey: descKey('snowflake-arctic-embed-l'),
	},
] as const
